import React, { useCallback, useEffect, useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";
import { FaEdit, FaTrash } from "react-icons/fa";
import axiosInstance from "../components/api/axiosInstance";
import PortalLocationSelects from "./PortalLocationSelects";
import PortalSourceSelect from "./PortalSourceSelect";
import { SupportSortableHeader } from "../pages/SupportSortableHeader";
import { canDeleteInPortal } from "./portalSession";
import { mobileValidationMessage, sanitizeMobileInput } from "./portalPhone";

const CUSTOMER_STATUSES = ["Active", "Inactive"];
const EMPTY_FORM = {
  firstName: "",
  lastName: "",
  email: "",
  countryCode: "+91",
  phone: "",
  altPhone: "",
  addressLine1: "",
  addressLine2: "",
  countryId: "",
  stateId: "",
  cityId: "",
  zipCode: "",
  source: "",
  comLang: "",
  status: "Active",
  notes: "",
};

function display(value) {
  if (value == null || String(value).trim() === "") return "—";
  return String(value);
}

function fullName(row) {
  return `${(row.First_Name || "").trim()} ${(row.Last_Name || "").trim()}`.trim() || "—";
}

function phoneDisplay(row) {
  const cc = row.Country_Code ? String(row.Country_Code).trim() : "";
  const phone = row.Phone ? String(row.Phone).trim() : "";
  if (!phone) return "—";
  return cc ? `${cc} ${phone}` : phone;
}

const SORT_ACCESSORS = {
  name: (c) => fullName(c).toLowerCase(),
  email: (c) => (c.Email || "").toLowerCase(),
  phone: (c) => phoneDisplay(c),
  status: (c) => (c.Status || "").toLowerCase(),
  source: (c) => (c.Source || "").toLowerCase(),
};

const COLUMNS = [
  { key: "name", label: "Name" },
  { key: "email", label: "Email" },
  { key: "phone", label: "Phone" },
  { key: "source", label: "Source" },
  { key: "status", label: "Status" },
];

const DATE_PRESETS = [
  { id: "all", label: "All time" },
  { id: "today", label: "Today" },
  { id: "7", label: "Last 7 days" },
  { id: "30", label: "Last 30 days" },
  { id: "month", label: "This month" },
  { id: "custom", label: "Custom" },
];

function startOfDay(d) {
  const x = new Date(d);
  x.setHours(0, 0, 0, 0);
  return x;
}

function endOfDay(d) {
  const x = new Date(d);
  x.setHours(23, 59, 59, 999);
  return x;
}

function presetRange(preset, fromDate, toDate) {
  const now = new Date();
  switch (preset) {
    case "today":
      return { from: startOfDay(now), to: endOfDay(now) };
    case "7": {
      const from = startOfDay(now);
      from.setDate(from.getDate() - 6);
      return { from, to: endOfDay(now) };
    }
    case "30": {
      const from = startOfDay(now);
      from.setDate(from.getDate() - 29);
      return { from, to: endOfDay(now) };
    }
    case "month":
      return { from: startOfDay(new Date(now.getFullYear(), now.getMonth(), 1)), to: endOfDay(now) };
    case "custom":
      return {
        from: fromDate ? startOfDay(new Date(fromDate)) : null,
        to: toDate ? endOfDay(new Date(toDate)) : null,
      };
    default:
      return { from: null, to: null };
  }
}

function CustomerTenantCustomers() {
  const navigate = useNavigate();
  const [customers, setCustomers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");
  const [sortKey, setSortKey] = useState("name");
  const [sortDir, setSortDir] = useState("asc");
  const [showAddModal, setShowAddModal] = useState(false);
  const [form, setForm] = useState(EMPTY_FORM);
  const [submitting, setSubmitting] = useState(false);
  const [formError, setFormError] = useState("");
  const [search, setSearch] = useState("");
  const [datePreset, setDatePreset] = useState("all");
  const [fromDate, setFromDate] = useState("");
  const [toDate, setToDate] = useState("");
  const [statusFilter, setStatusFilter] = useState("");
  const [sourceFilter, setSourceFilter] = useState("");
  const [cityFilter, setCityFilter] = useState("");
  const [sourceOptions, setSourceOptions] = useState([]);
  const [deactivatingId, setDeactivatingId] = useState(null);
  const canDelete = useMemo(() => canDeleteInPortal(), []);

  const toggleSort = (key) => {
    if (key === sortKey) {
      setSortDir((d) => (d === "asc" ? "desc" : "asc"));
    } else {
      setSortKey(key);
      setSortDir("asc");
    }
  };

  const loadCustomers = useCallback(async () => {
    const res = await axiosInstance.get("/client/tenant-customers");
    setCustomers(Array.isArray(res.data?.data) ? res.data.data : []);
  }, []);

  useEffect(() => {
    let active = true;
    (async () => {
      try {
        await loadCustomers();
      } catch (_) {
        if (active) setError("Could not load customers.");
      } finally {
        if (active) setLoading(false);
      }
    })();
    return () => {
      active = false;
    };
  }, [loadCustomers]);

  useEffect(() => {
    let active = true;
    (async () => {
      try {
        const res = await axiosInstance.get("/client/lead-meta");
        if (!active) return;
        const sources = Array.isArray(res.data?.data?.sources) ? res.data.data.sources : [];
        setSourceOptions(
          sources
            .map((s) => s.Source_Name ?? s.source_name ?? "")
            .filter(Boolean)
            .sort((a, b) => a.localeCompare(b))
        );
      } catch (_) {
        if (active) setSourceOptions([]);
      }
    })();
    return () => {
      active = false;
    };
  }, []);

  const cityOptions = useMemo(
    () =>
      Array.from(new Set(customers.map((c) => c.City_Name).filter(Boolean))).sort((a, b) =>
        a.localeCompare(b)
      ),
    [customers]
  );

  const filtered = useMemo(() => {
    let list = customers;

    const { from, to } = presetRange(datePreset, fromDate, toDate);
    if (from || to) {
      list = list.filter((c) => {
        if (!c.Created_On) return false;
        const created = new Date(c.Created_On);
        if (Number.isNaN(created.getTime())) return false;
        if (from && created < from) return false;
        if (to && created > to) return false;
        return true;
      });
    }

    if (statusFilter) list = list.filter((c) => c.Status === statusFilter);
    if (sourceFilter) list = list.filter((c) => c.Source === sourceFilter);
    if (cityFilter) list = list.filter((c) => c.City_Name === cityFilter);

    const q = search.trim().toLowerCase();
    if (q) {
      list = list.filter((c) =>
        [
          c.First_Name,
          c.Last_Name,
          c.Email,
          c.Phone,
          c.Alt_Phone,
          c.Country_Code,
          c.Source,
          c.City_Name,
          c.State_Name,
          c.Country_Name,
        ]
          .filter(Boolean)
          .some((v) => String(v).toLowerCase().includes(q))
      );
    }

    return list;
  }, [customers, search, datePreset, fromDate, toDate, statusFilter, sourceFilter, cityFilter]);

  const hasActiveFilters =
    datePreset !== "all" ||
    !!statusFilter ||
    !!sourceFilter ||
    !!cityFilter ||
    !!search;

  const clearFilters = () => {
    setDatePreset("all");
    setFromDate("");
    setToDate("");
    setStatusFilter("");
    setSourceFilter("");
    setCityFilter("");
    setSearch("");
  };

  const sorted = useMemo(() => {
    const accessor = SORT_ACCESSORS[sortKey];
    if (!accessor) return filtered;
    const dir = sortDir === "asc" ? 1 : -1;
    return [...filtered].sort((a, b) => {
      const av = accessor(a);
      const bv = accessor(b);
      if (av < bv) return -1 * dir;
      if (av > bv) return 1 * dir;
      return 0;
    });
  }, [filtered, sortKey, sortDir]);

  const setField = (name, value) => setForm((f) => ({ ...f, [name]: value }));

  const closeAddModal = () => {
    setShowAddModal(false);
    setForm(EMPTY_FORM);
    setFormError("");
  };

  const buildPayload = () => ({
    First_Name: form.firstName.trim(),
    Last_Name: form.lastName.trim() || null,
    Email: form.email.trim() || null,
    Country_Code: form.countryCode.trim() || null,
    Phone: form.phone.trim() || null,
    Alt_Phone: form.altPhone.trim() || null,
    Address_Line_1: form.addressLine1.trim() || null,
    Address_Line_2: form.addressLine2.trim() || null,
    Country_Id: form.countryId ? Number(form.countryId) : null,
    State_Id: form.stateId ? Number(form.stateId) : null,
    City_Id: form.cityId ? Number(form.cityId) : null,
    Zip_Code: form.zipCode.trim() || null,
    Source: form.source.trim() || null,
    Com_Lang: form.comLang.trim() || null,
    Status: form.status || "Active",
    Notes: form.notes.trim() || null,
  });

  const handleDeactivate = async (customer) => {
    if (customer.Status === "Inactive") return;
    if (!window.confirm(`Deactivate ${fullName(customer)}? Their status will be set to Inactive.`)) return;

    setDeactivatingId(customer.Tenant_Customer_Id);
    setError("");
    try {
      await axiosInstance.delete(`/client/tenant-customers/${customer.Tenant_Customer_Id}`);
      await loadCustomers();
    } catch (err) {
      setError(err.response?.data?.message || "Could not deactivate customer.");
    } finally {
      setDeactivatingId(null);
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!form.firstName.trim()) {
      setFormError("First name is required.");
      return;
    }
    const phoneError = mobileValidationMessage(form.phone, {
      required: true,
      label: "Mobile number",
    });
    if (phoneError) {
      setFormError(phoneError);
      return;
    }
    const altPhoneError = mobileValidationMessage(form.altPhone, { label: "Alternate phone" });
    if (altPhoneError) {
      setFormError(altPhoneError);
      return;
    }
    if (!form.source.trim()) {
      setFormError("Source is required.");
      return;
    }

    setSubmitting(true);
    setFormError("");
    try {
      await axiosInstance.post("/client/tenant-customers", buildPayload());
      await loadCustomers();
      closeAddModal();
    } catch (err) {
      setFormError(
        err.response?.data?.message ||
          err.response?.data?.error ||
          "Could not create customer."
      );
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div className="cportal-page">
      <div className="cportal-page-head">
        <h1>Customers</h1>
        <button
          type="button"
          className="cportal-btn cportal-btn-primary"
          onClick={() => setShowAddModal(true)}
        >
          Add customer
        </button>
      </div>

      {error && <div className="cportal-alert cportal-alert-error">{error}</div>}

      <div className="cportal-filters">
        <div className="cportal-filter-presets">
          {DATE_PRESETS.map((p) => (
            <button
              key={p.id}
              type="button"
              className={`cportal-chip ${datePreset === p.id ? "active" : ""}`}
              onClick={() => setDatePreset(p.id)}
            >
              {p.label}
            </button>
          ))}
        </div>

        {datePreset === "custom" && (
          <div className="cportal-filter-dates">
            <label className="cportal-field">
              <span>From date</span>
              <input type="date" value={fromDate} onChange={(e) => setFromDate(e.target.value)} />
            </label>
            <label className="cportal-field">
              <span>To date</span>
              <input type="date" value={toDate} onChange={(e) => setToDate(e.target.value)} />
            </label>
          </div>
        )}

        <div className="cportal-filter-row">
          <input
            className="cportal-search cportal-filter-search"
            type="text"
            placeholder="Search name, phone, email"
            value={search}
            onChange={(e) => setSearch(e.target.value)}
          />
          <select value={statusFilter} onChange={(e) => setStatusFilter(e.target.value)}>
            <option value="">All status</option>
            {CUSTOMER_STATUSES.map((s) => (
              <option key={s} value={s}>
                {s}
              </option>
            ))}
          </select>
          <select value={sourceFilter} onChange={(e) => setSourceFilter(e.target.value)}>
            <option value="">All sources</option>
            {sourceOptions.map((s) => (
              <option key={s} value={s}>
                {s}
              </option>
            ))}
          </select>
          <select value={cityFilter} onChange={(e) => setCityFilter(e.target.value)}>
            <option value="">All cities</option>
            {cityOptions.map((s) => (
              <option key={s} value={s}>
                {s}
              </option>
            ))}
          </select>
          {hasActiveFilters && (
            <button type="button" className="cportal-clear-filters" onClick={clearFilters}>
              Clear
            </button>
          )}
        </div>
      </div>

      <div className="cportal-card">
        <div className="cportal-card-head">
          <span className="cportal-muted">{sorted.length} customer(s)</span>
        </div>

        {loading ? (
          <p className="cportal-muted">Loading…</p>
        ) : sorted.length === 0 ? (
          <p className="cportal-muted">
            {customers.length === 0 ? "No customers added yet." : "No customers match your filters."}
          </p>
        ) : (
          <div className="cportal-table-wrap">
            <table className="cportal-table">
              <thead>
                <tr>
                  {COLUMNS.map((col) => (
                    <th key={col.key} className="cportal-th-sortable">
                      <SupportSortableHeader
                        columnId={col.key}
                        label={col.label}
                        sortColumn={sortKey}
                        sortDirection={sortDir}
                        onSort={toggleSort}
                      />
                    </th>
                  ))}
                  <th className="cportal-th-actions">Actions</th>
                </tr>
              </thead>
              <tbody>
                {sorted.map((customer) => (
                  <tr key={customer.Tenant_Customer_Id}>
                    <td>
                      <button
                        type="button"
                        className="cportal-table-link"
                        onClick={() =>
                          navigate(`/portal/customers/${customer.Tenant_Customer_Id}`)
                        }
                      >
                        {fullName(customer)}
                      </button>
                    </td>
                    <td>{display(customer.Email)}</td>
                    <td>{phoneDisplay(customer)}</td>
                    <td>{display(customer.Source)}</td>
                    <td>{display(customer.Status)}</td>
                    <td>
                      <div className="cportal-re-actions">
                        <button
                          type="button"
                          className="re-lead-action re-lead-action--edit"
                          onClick={() =>
                            navigate(`/portal/customers/${customer.Tenant_Customer_Id}`, {
                              state: { edit: true },
                            })
                          }
                          title="Edit customer"
                          aria-label="Edit customer"
                        >
                          <FaEdit size={16} />
                        </button>
                        {canDelete && (
                          <button
                            type="button"
                            className="re-lead-action re-lead-action--delete"
                            onClick={() => handleDeactivate(customer)}
                            disabled={
                              customer.Status === "Inactive" ||
                              deactivatingId === customer.Tenant_Customer_Id
                            }
                            title={
                              customer.Status === "Inactive"
                                ? "Already inactive"
                                : "Deactivate customer"
                            }
                            aria-label="Deactivate customer"
                          >
                            {deactivatingId === customer.Tenant_Customer_Id ? "…" : <FaTrash size={16} />}
                          </button>
                        )}
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>

      {showAddModal && (
        <div className="cportal-modal-overlay" onClick={closeAddModal} role="presentation">
          <div
            className="cportal-modal cportal-modal--form cportal-modal--wide"
            onClick={(e) => e.stopPropagation()}
            role="dialog"
            aria-labelledby="add-tenant-customer-title"
          >
            <div className="cportal-modal-header">
              <h3 id="add-tenant-customer-title">Add customer</h3>
              <button type="button" onClick={closeAddModal} aria-label="Close">
                ✕
              </button>
            </div>
            <form className="cportal-modal-body" onSubmit={handleSubmit}>
              {formError && (
                <div className="cportal-alert cportal-alert-error">{formError}</div>
              )}
              <div className="cportal-form-grid cportal-form-grid--3">
                <div className="cportal-field">
                  <label>
                    First name <span className="cportal-required">*</span>
                  </label>
                  <input
                    value={form.firstName}
                    onChange={(e) => setField("firstName", e.target.value)}
                    required
                  />
                </div>
                <div className="cportal-field">
                  <label>Last name</label>
                  <input
                    value={form.lastName}
                    onChange={(e) => setField("lastName", e.target.value)}
                  />
                </div>
                <div className="cportal-field">
                  <label>Email</label>
                  <input
                    type="email"
                    value={form.email}
                    onChange={(e) => setField("email", e.target.value)}
                  />
                </div>
                <div className="cportal-field cportal-field-phone">
                  <label>
                    Mobile number <span className="cportal-required">*</span>
                  </label>
                  <div className="cportal-phone-row">
                    <input
                      className="cportal-phone-code"
                      value={form.countryCode}
                      onChange={(e) => setField("countryCode", e.target.value)}
                      placeholder="+91"
                    />
                    <input
                      value={form.phone}
                      onChange={(e) => setField("phone", sanitizeMobileInput(e.target.value))}
                      inputMode="numeric"
                      maxLength={10}
                      placeholder="10-digit number"
                      required
                    />
                  </div>
                </div>
                <div className="cportal-field">
                  <label>Alt phone</label>
                  <input
                    value={form.altPhone}
                    onChange={(e) => setField("altPhone", sanitizeMobileInput(e.target.value))}
                    inputMode="numeric"
                    maxLength={10}
                    placeholder="10-digit number"
                  />
                </div>
                <PortalSourceSelect
                  value={form.source}
                  onChange={(value) => setField("source", value)}
                  required
                />
                <div className="cportal-field">
                  <label>Communication language</label>
                  <input
                    value={form.comLang}
                    onChange={(e) => setField("comLang", e.target.value)}
                  />
                </div>
                <div className="cportal-field">
                  <label>
                    Status <span className="cportal-required">*</span>
                  </label>
                  <select value={form.status} onChange={(e) => setField("status", e.target.value)}>
                    {CUSTOMER_STATUSES.map((s) => (
                      <option key={s} value={s}>
                        {s}
                      </option>
                    ))}
                  </select>
                </div>
              </div>

              <h3 className="cportal-form-section">Address</h3>
              <div className="cportal-form-grid cportal-form-grid--3">
                <div className="cportal-field">
                  <label>Address line 1</label>
                  <input
                    value={form.addressLine1}
                    onChange={(e) => setField("addressLine1", e.target.value)}
                  />
                </div>
                <div className="cportal-field">
                  <label>Address line 2</label>
                  <input
                    value={form.addressLine2}
                    onChange={(e) => setField("addressLine2", e.target.value)}
                  />
                </div>
                <PortalLocationSelects
                  countryId={form.countryId}
                  stateId={form.stateId}
                  cityId={form.cityId}
                  onChange={({ countryId, stateId, cityId }) =>
                    setForm((f) => ({ ...f, countryId, stateId, cityId }))
                  }
                />
                <div className="cportal-field">
                  <label>Zip code</label>
                  <input
                    value={form.zipCode}
                    onChange={(e) => setField("zipCode", e.target.value)}
                  />
                </div>
              </div>

              <div className="cportal-form-grid cportal-form-grid--3">
                <div className="cportal-field cportal-field--full">
                  <label>Notes</label>
                  <textarea
                    rows={3}
                    value={form.notes}
                    onChange={(e) => setField("notes", e.target.value)}
                  />
                </div>
              </div>
              <div className="cportal-form-actions">
                <button
                  type="button"
                  className="cportal-btn cportal-btn-ghost"
                  onClick={closeAddModal}
                  disabled={submitting}
                >
                  Cancel
                </button>
                <button type="submit" className="cportal-btn cportal-btn-primary" disabled={submitting}>
                  {submitting ? "Creating…" : "Create customer"}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}
    </div>
  );
}

export default CustomerTenantCustomers;
